import React, { useState } from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Section from "src/components/Section"
import Grid from "src/components/Grid"
import Button from "src/components/Button"
import Link from "src/components/Link"
import Media from "src/components/Media"
import Image from "src/components/Image"
import Slider from "react-slick"
import TextLockup from "src/components/TextLockup"
import { globals, colors, mq, typography } from "src/styles"
import { MdOpenInNew } from "react-icons/md"

const Wrapper = styled(Section)`
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
    `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
`

const IntroTextWrapper = styled(Grid)`
  ${globals.verticalSpacing("padding-bottom", 0.5)}
  h1, h2 {
    max-width: 12em;
  }
  h3 {
    max-width: 18em;
  }
  h4,
  h5 {
    max-width: 24em;
  }
  h6 {
    max-width: 16em;
  }
  p {
    max-width: 36em;
  }
`

const BottomActions = styled(Grid)`
  ${globals.verticalSpacing("padding-top", 0.5)}
`

const PartnerSlide = styled.div`
  ${mq.largeAndUp} {
    height: 100%;
    .gatsby-image-wrapper {
      height: 100%;
    }
  }
`

const SliderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 20px;
  > button:first-of-type {
    margin-right: 10px;
  }
`

const CollageWrapper = styled.div`
  position: relative;
  text-align: right;
`

const LargeSliderWrap = styled.div`
  position: relative;
  z-index: 1;
  ${mq.mediumAndUp} {
    border-radius: var(--card-border-radius);
    overflow: hidden;
  }
  ${mq.largeAndUp} {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 32%;
    margin-right: 0;
    .slick-slider,
    .slick-list,
    .slick-track {
      height: 100%;
    }
    .slick-slide > div {
      height: 100%;
    }
  }
  ${mq.largerAndUp} {
    right: 27%;
  }
`

const CardSliderWControls = styled.div`
  position: relative;
  z-index: 2;
  margin: -20% auto 0;
  width: calc(100% - var(--site-margins) * 2);
  max-width: 500px;
  text-align: left;
  ${mq.mediumAndUp} {
    width: calc(100% - 100% / 7);
  }
  ${mq.largeAndUp} {
    display: inline-block;
    vertical-align: top;
    margin: 0;
    position: relative;
    right: 0;
    margin: 10% 0;
    width: 36%;
    min-width: 300px;
  }
`

const MiniSliderWrap = styled(globals.Card)`
  background: ${colors.white};
  overflow: hidden;
  .slick-slider,
  .slick-list {
    border-radius: var(--card-border-radius);
    overflow: clip;
  }
`

const FeaturedPartners = ({
  className,
  nextTheme,
  prevTheme,
  theme,
  spacing,
  isFirstSection,
  headline,
  introText,
  actions,
  partners,
  id,
  hideMobile,
  hideDesktop,
}) => {
  const [slider, setSlider] = useState(null)
  const [miniSlider, setMiniSlider] = useState(null)

  const settings = {
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: true,
    swipe: true,
    speed: 500,
  }

  const miniSettings = {
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: true,
    swipe: true,
    speed: 500,
  }

  return (
    <Wrapper
      className={className}
      prevTheme={prevTheme}
      setTheme={theme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      {(introText?.eyebrow || introText?._rawText) && (
        <IntroTextWrapper small="container" medium="1 [12] 1">
          <TextLockup
            eyebrow={introText.eyebrow}
            newStyle={introText?.newStyle}
            text={introText._rawText}
            alignment="center"
            theme={theme}
          />
        </IntroTextWrapper>
      )}

      <Grid
        small="[1]"
        medium="1 [12] 1"
        large="2 [10] 2"
        larger="5 [18] 5"
        extraLarge="3 [8] 3"
      >
        <CollageWrapper>
          <LargeSliderWrap>
            <Slider
              ref={slider => setSlider(slider)}
              {...settings}
              asNavFor={miniSlider}
            >
              {partners.map((partner, index) => (
                <PartnerSlide key={partner._key}>
                  <Media
                    media={partner.media}
                    ratio={0.85}
                    altText={partner.title + " slide " + (index + 1)}
                  />
                </PartnerSlide>
              ))}
            </Slider>
          </LargeSliderWrap>

          <CardSliderWControls>
            <MiniSliderWrap>
              <Slider
                ref={miniSlider => setMiniSlider(miniSlider)}
                {...miniSettings}
                asNavFor={slider}
              >
                {partners.map((partner, index) => (
                  <div key={partner._key + "-mini"}>
                    <Image
                      image={partner?.partnerLogo?.asset?.gatsbyImageData}
                      ratio={9 / 16}
                      alt={partner.title}
                    />
                    <div
                      css={css`
                        padding: 16px 20px 20px 20px;
                      `}
                    >
                      {partner.text && (
                        <p
                          css={css`
                            color: ${colors.textColor} !important;
                            ${typography.bodyMedium};
                            font-weight: ${typography.bold};
                            margin: 0;
                          `}
                        >
                          {partner.text}
                        </p>
                      )}
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          padding-top: 16px;
                          p {
                            margin: 0;
                          }
                          a {
                            display: block;
                            color: ${colors.mainColor};
                            svg {
                              display: block;
                            }
                          }
                        `}
                      >
                        <p>{partner.title}</p>
                        {partner.partnerLink && (
                          <Link
                            to={partner.partnerLink}
                            external={true}
                            target="_blank"
                          >
                            <MdOpenInNew size={24} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </MiniSliderWrap>

            <SliderControls>
              <Button
                icon="arrow_back"
                onClick={() => slider?.slickPrev()}
                shape="circle"
                title="Go to previous slide"
                setTheme="white"
              />
              <Button
                icon="arrow_forward"
                onClick={() => slider?.slickNext()}
                shape="circle"
                title="Go to next slide"
                setTheme="white"
              />
            </SliderControls>
          </CardSliderWControls>
        </CollageWrapper>
      </Grid>

      {actions && actions?.length > 0 && (
        <BottomActions small="container" medium="1 [12] 1">
          <TextLockup alignment="center" actions={actions} />
        </BottomActions>
      )}
    </Wrapper>
  )
}

export default FeaturedPartners
